import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import Card from "../../common/components/Card";
import Checkbox from "../../common/components/Checkbox";
import { Col, Row } from "../../common/components/Grid";
import Icon from "../../common/components/Icon";
import ReactForm from "../../common/components/ReactForm";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { approveAccountRequest, canCreateRequest, canUpdateRequest, createAccountRequest, rejectAccountRequest, updateAccountRequest, useAccountRequests } from "../../reducers/accountRequestSlice";
import { LogPermissionsInput, SitRepPermissionsInput, UserPermissionsInput } from "./PermissionsInput";
import SimpleSettings from "./SimpleSettings";
import PropTypes from "prop-types";
import AccountRequestDetails from "../AccountRequestDetails";



export const label = t => t('accountRequest_other')
const defaultSort = ['firstname'];
const newRequestTemplate = { role: [], sitrep_access: [], log_access: [], customfields: {}   };

const AccountRequestLabel = ({ value: request }) => {
    return <Row className="px-2">
            <Col className="text-left">
                {request.firstname} {request.lastname}
            </Col>
        </Row>
}

const AdminAccountRequestDetails = forwardRef(({ value: request, disabled, onChange }, ref) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    return <>
        <ReactForm ref={ref}>
            <AccountRequestDetails value={request} disabled={disabled} onChange={onChange}></AccountRequestDetails>
        </ReactForm>
        <UserPermissionsInput disabled={disabled} values={request?.role} onApply={role => onChange({ role })}></UserPermissionsInput>
        {clientConfig.sitrep?.enable && <SitRepPermissionsInput disabled={disabled} values={request?.sitrep_access} onApply={sitrep_access => onChange({ sitrep_access })}></SitRepPermissionsInput>}
        {clientConfig.enableLog && <LogPermissionsInput disabled={disabled} values={request?.log_access} onApply={log_access => onChange({ log_access })}></LogPermissionsInput>}
        <Card title={t('options')}>
            <Row>
                <Col md={3}><Checkbox checked={!!request?.disabled} disabled={disabled} onChange={disabled => onChange({ disabled })} placeholder={t('disabled')}></Checkbox></Col>
                <Col md={3}><Checkbox checked={!!request?.reset_required} disabled={disabled} onChange={reset_required => onChange({ reset_required })} placeholder={t('resetRequired')}></Checkbox></Col>
            </Row>
        </Card>
    </>
})

const AccountRequests = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const requests = useAccountRequests();
    const sortFields = useMemo(() => ({
        firstname: t('firstname'),
        lastname: t('lastname'),
    }), [t]);

    const handleApprove = useCallback((selectedRequest, selectItem) => {
        approveAccountRequest(network, selectedRequest).then(() => selectItem())
    }, [network]);

    const handleReject = useCallback((selectedRequest, selectItem) => {
        rejectAccountRequest(network, selectedRequest).then(() => selectItem())
    }, [network]);

    return <SimpleSettings data={requests} uuid={uuid} label={label} pkey="id" defaultSort={defaultSort} sortFields={sortFields}
        templateItem={newRequestTemplate}
        canAdd={request => canCreateRequest(credentials)} canUpdate={request => canUpdateRequest(credentials, request)}
        canRemove={request => false}
        onAdd={request => createAccountRequest(network, request)}
        onChange={(request, delta) => updateAccountRequest(network, { id: request.id, ...delta })}
        onClose={onClose} ItemLabel={AccountRequestLabel} ItemDetails={AdminAccountRequestDetails}
        additionalControls={(item, selectItem) => item?.id && <>
            <Button className="mx-2" variant="success" onClick={() => handleApprove(item, selectItem)}><Icon intent="ok"></Icon> {t('approve')}</Button>
            <Button variant="danger" confirmMessage={t('confirmRejectAccountRequest')} onClick={() => handleReject(item, selectItem)}><Icon intent="cancel"></Icon> {t('reject')}</Button>
        </>}
    ></SimpleSettings>
}
AccountRequests.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}
export default AccountRequests;